import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import React, { memo, useEffect, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { formatMessageHelper } from '../../../shared/helpers/internationalization';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { DropdownField } from '../Filters/FilterFields/DropdownField';
import { useSelector } from 'react-redux';
import { editorOptionsSelector } from '../../../reducers/EditorReducer/EditorSelector';
import { GenericFieldTransformer } from '../Filters/FieldTransformers/FieldTransformers';
import './CommondataFieldsSelector.scss';
import { useFilterController } from '../Filters/useFilterForm';
import { FiltersControl } from '../Filters/types';
import classcat from 'classcat';
import { CommonDataFields } from '../../../shared/api/apiHooks';

const formatOptionsForDropdown = (options: { value: any; label: any }[]) =>
  options?.map(({ value, label }) => ({ value, text: label })) ?? [{ value: undefined, text: '' }];

export const useCommonDataFields = (control: Control) => {
  const editorOptions = (useSelector(editorOptionsSelector) as any)?.toJS();
  const [currentInstrument, currentRegion, currentDelay, currentUniverse] = useWatch({
    control,
    name: [filterFields.instrument.name, filterFields.region.name, filterFields.delay.name, filterFields.universe.name],
  });

  const delayControl = useFilterController({ control, name: filterFields.delay.name });
  const universeControl = useFilterController({ control, name: filterFields.universe.name });

  const dataExplorerCommonFields = useMemo(
    () => ({
      region: formatOptionsForDropdown(editorOptions?.region.choices.instrumentType[currentInstrument]),
      universe: formatOptionsForDropdown(editorOptions?.universe.choices.instrumentType[currentInstrument]?.region[currentRegion]),
      delay: formatOptionsForDropdown(editorOptions?.delay.choices.instrumentType[currentInstrument]?.region[currentRegion])?.map(
        delay => ({
          ...delay,
          value: String(delay.value),
        })
      ),
    }),
    [currentRegion, currentInstrument, currentRegion, editorOptions]
  );

  useEffect(() => {
    const isFieldCorrect = dataExplorerCommonFields.delay.find(item => item.value === currentDelay);
    if (!isFieldCorrect) {
      delayControl.field.onChangeOriginal({ target: { value: dataExplorerCommonFields.delay[0].value } });
    }
  }, [currentDelay, dataExplorerCommonFields]);
  useEffect(() => {
    const isFieldCorrect = dataExplorerCommonFields.universe.find(item => item.value === currentUniverse);
    if (!isFieldCorrect) {
      universeControl.field.onChangeOriginal({ target: { value: dataExplorerCommonFields.universe[0].value } });
    }
  }, [currentUniverse, dataExplorerCommonFields]);

  return dataExplorerCommonFields;
};

export const CommonDataFieldsSelector = injectIntl(
  memo(
    ({
      intl,
      control,
      classModifier,
      className,
    }: { control: FiltersControl; classModifier?: string; className?: string } & InjectedIntlProps) => {
      const dataExplorerCommonFields = useCommonDataFields(control);

      return (
        <div className={classcat(['data-common-fields', className])}>
          <DropdownField
            key="region"
            classModifier={classModifier}
            controllerProps={{ name: filterFields.region.name, control }}
            options={dataExplorerCommonFields.region}
            title={formatMessageHelper(intl, 'dataExplorer.filters.regionLabel')}
          />

          <DropdownField
            key="delay"
            classModifier={classModifier}
            title={formatMessageHelper(intl, 'dataExplorer.filters.delayLabel')}
            controllerProps={{ name: filterFields.delay.name, control }}
            options={dataExplorerCommonFields.delay}
          />

          <DropdownField
            key="universe"
            classModifier={classModifier}
            size="large"
            title={formatMessageHelper(intl, 'dataExplorer.filters.universeLabel')}
            controllerProps={{ name: filterFields.universe.name, control }}
            options={dataExplorerCommonFields.universe}
          />
        </div>
      );
    }
  )
);
export const COMMON_DATA_FIELDS = 'commonDataFilterPreferences__v1';

const DEFAULT_COMMON_DATA_FILTER_VALUES = {
  [filterFields.region.name]: filterFields.region.defaultValue,
  [filterFields.delay.name]: filterFields.delay.defaultValue,
  [filterFields.universe.name]: filterFields.universe.defaultValue,
  [filterFields.instrument.name]: filterFields.instrument.defaultValue,
};

export function getDefaultCommonDataFilterValues(): CommonDataFields {
  const localStorageItem = localStorage.getItem(COMMON_DATA_FIELDS);
  return localStorageItem ? JSON.parse(localStorageItem) : DEFAULT_COMMON_DATA_FILTER_VALUES;
}

export function setRegionDelayValues({ region, delay, universe, instrumentType }: CommonDataFields) {
  localStorage.setItem(COMMON_DATA_FIELDS, JSON.stringify({ region, delay, universe, instrumentType }));
}

export const commonFieldTransformers = [
  new GenericFieldTransformer(filterFields.region.name),
  new GenericFieldTransformer(filterFields.delay.name),
  new GenericFieldTransformer(filterFields.instrument.name),
  new GenericFieldTransformer(filterFields.universe.name),
];

export const useSaveCommonFieldsToLocalStorage = (pageQueryObject: Partial<CommonDataFields>) => {
  // sync region and delay between forms
  useEffect(() => {
    const region = pageQueryObject[filterFields.region.name];
    const delay = pageQueryObject[filterFields.delay.name];
    const universe = pageQueryObject[filterFields.universe.name];
    const instrumentType = pageQueryObject[filterFields.instrument.name];
    if (region && delay && universe && instrumentType) {
      setRegionDelayValues({ region, delay: delay.toString(), universe, instrumentType });
    }
  }, [pageQueryObject]);
};

export const useWatchCommonDataFields = (control: FiltersControl) => {
  const [region, delay, universe, instrumentType] = useWatch({
    control,
    name: [filterFields.region.name, filterFields.delay.name, filterFields.universe.name, filterFields.instrument.name],
  });

  const result = useMemo(() => ({ region, delay, universe, instrumentType }), [region, delay, universe, instrumentType]);
  return result;
};
